/**
 * The ship request that "Generate Labels" / "Ship Bundle" started, kept OUTSIDE any page.
 *
 * WHY. The ship is one synchronous server call — carrier label, inventory transaction, post-commit
 * dispatch — that takes seconds. It used to run under a full-screen "Processing… don't go back"
 * spinner, and only once it returned did the operator reach the Documents page, where the PDFs then
 * still had to be generated. Now the Shipping page starts the request here and navigates to the
 * Documents page IMMEDIATELY; that page watches this run and fills in each document as it lands —
 * the carrier label first (it is filed before the ship transaction even starts), then the SSCC
 * label, then the rest.
 *
 * Holding the request in a module-level store (not in the Shipping page's state) is what makes that
 * safe: unmounting the page does not cancel an Apollo mutation, and the run's outcome must still be
 * readable by whichever screen is showing that order when it settles. A full reload abandons the
 * client side only — the server finishes the ship regardless — and the Documents page then sees the
 * order at `shipping` with no run and waits for it to flip (see `shipPhaseFor`).
 *
 * NOTHING HERE CHANGES WHAT THE SERVER DOES. It is the same mutation with the same variables; only
 * the moment the operator gets to see the documents moved.
 */
import { useEffect, useState } from 'react';
import { createStore, useStore } from 'zustand';

export type ShipRunStatus = 'running' | 'succeeded' | 'failed';

/**
 * What the operator entered on the Shipping page, kept so that a FAILED ship sends them back to a
 * page that still holds it instead of an empty form. Opaque to this module.
 */
export type ShipDraft = Record<string, unknown>;

export type ShipRun = {
  /** The order whose Shipping page started it. */
  orderId: string;
  /** Every order this run ships: the order itself, or each member of a bundle. */
  orderIds: string[];
  kind: 'order' | 'bundle';
  /** True when the server buys a carrier (UPS/FedEx) label — false for FOB and freight. */
  buysCarrierLabel: boolean;
  label: string;
  status: ShipRunStatus;
  startedAt: number;
  finishedAt?: number;
  error?: string;
  /**
   * A failure that did NOT come from the server's answer — dropped wifi, a gateway timeout, the
   * device sleeping mid-request. The server may well have finished the ship anyway, so the page
   * keeps checking the order instead of declaring "nothing was shipped".
   */
  outcomeUnknown?: boolean;
  draft?: ShipDraft;
  /**
   * Where a successful ship takes the operator (see @/lib/shipHandoff): `home` — the Shipping page
   * waits in place, then goes to the home screen — or `documents`, the page the ship lands on at
   * once. Absent means `documents`.
   */
  afterShip?: 'home' | 'documents';
};

/**
 * A failed run stops describing the order after this long — by then the operator has moved on, and
 * a stale "Could not ship" must not stick to an order someone has since shipped another way (for a
 * bundle, a member that was later taken out and shipped alone).
 */
export const FAILED_RUN_TTL_MS = 10 * 60 * 1000;

type ShipRunState = {
  runs: Record<string, ShipRun>;
  /** How many mounted screens are showing each order's run (see `useWatchShipRun`). */
  watchers: Record<string, number>;
};

export const shipRunStore = createStore<ShipRunState>(() => ({ runs: {}, watchers: {} }));

const putRun = (run: ShipRun) =>
  shipRunStore.setState((state) => ({
    runs: {
      ...state.runs,
      ...Object.fromEntries(run.orderIds.map((id) => [id, run]))
    }
  }));

export const getShipRun = (orderId?: string | null): ShipRun | undefined =>
  orderId ? shipRunStore.getState().runs[String(orderId)] : undefined;

export const isShipRunning = (orderId?: string | null) => getShipRun(orderId)?.status === 'running';

/** Forget a run (every order it covered). */
export const clearShipRun = (orderId?: string | null) => {
  const run = getShipRun(orderId);
  if (!run) return;
  shipRunStore.setState((state) => {
    const runs = { ...state.runs };
    run.orderIds.forEach((id) => {
      if (runs[id] === run) delete runs[id];
    });
    return { runs };
  });
};

export const errorMessageOf = (e: any, fallback = 'Failed to generate shipping labels.') =>
  e?.graphQLErrors?.[0]?.message || e?.networkError?.message || e?.message || fallback;

/** True only when the server itself answered with an error (so nothing was shipped). */
const serverRefused = (e: any) => Array.isArray(e?.graphQLErrors) && e.graphQLErrors.length > 0;

/**
 * Start a ship. Returns false — and does nothing — when one is already running for any of the
 * orders, so a double tap (or Back → Generate Labels again mid-ship) can never send a second
 * request that might buy a second label.
 *
 * `onSettled` fires once with the outcome; `watched` says whether a screen was showing the order at
 * that moment, so the caller can toast only when nobody would otherwise see it.
 */
export function startShipRun(
  spec: Omit<ShipRun, 'status' | 'startedAt' | 'finishedAt' | 'error'>,
  request: () => Promise<unknown>,
  onSettled?: (run: ShipRun, watched: boolean) => void
): boolean {
  const orderIds = Array.from(new Set([spec.orderId, ...spec.orderIds].map(String)));
  if (orderIds.some((id) => isShipRunning(id))) return false;
  const run: ShipRun = { ...spec, orderIds, status: 'running', startedAt: Date.now() };
  putRun(run);

  const settle = (patch: Partial<ShipRun>) => {
    const settled: ShipRun = { ...run, ...patch, finishedAt: Date.now() };
    putRun(settled);
    const { watchers } = shipRunStore.getState();
    onSettled?.(settled, orderIds.some((id) => (watchers[id] || 0) > 0));
  };

  // Deferred a tick so the caller's navigate() commits first, whatever the request does.
  Promise.resolve()
    .then(request)
    .then(
      () => settle({ status: 'succeeded' }),
      (e) =>
        settle({ status: 'failed', error: errorMessageOf(e), outcomeUnknown: !serverRefused(e) })
    );
  return true;
}

/** The run covering this order, live. */
export const useShipRun = (orderId?: string | null): ShipRun | undefined =>
  useStore(shipRunStore, (state) => (orderId ? state.runs[String(orderId)] : undefined));

/**
 * Register the calling screen as showing this order's run while it is mounted.
 *
 * When the last screen showing a SUCCEEDED run goes away, the run is forgotten: it has done its
 * job, and a later reprint visit to that order must not replay the shipping progress. Checked a
 * tick later so that moving between the members of one bundle (one screen out, the next in) does
 * not drop it mid-way. Running and failed runs are kept — the Shipping page still needs those.
 */
export const watchShipRun = (orderId: string) => {
  const bump = (by: number) =>
    shipRunStore.setState((state) => {
      const watchers = { ...state.watchers };
      const next = (watchers[orderId] || 0) + by;
      if (next > 0) watchers[orderId] = next;
      else delete watchers[orderId];
      return { watchers };
    });
  bump(1);
  return () => {
    bump(-1);
    setTimeout(() => {
      const run = getShipRun(orderId);
      if (run?.status !== 'succeeded') return;
      const { watchers } = shipRunStore.getState();
      if (run.orderIds.every((id) => !watchers[id])) clearShipRun(orderId);
    }, 0);
  };
};

/**
 * Where an order stands, for the Documents page.
 *  - `running`  — this device's request is in flight.
 *  - `failed`   — this device's request failed; nothing shipped.
 *  - `shipped`  — the server says so, or this device's request returned success (the page reads a
 *                 secondary, which may lag the commit by a moment).
 *  - `pending`  — the order is still at `shipping` and THIS device has no request for it: the page
 *                 was reloaded mid-ship, or the ship was started elsewhere. The server finishes on
 *                 its own, so the page keeps checking.
 */
export type ShipPhase = 'running' | 'failed' | 'shipped' | 'pending';

export function shipPhaseFor(
  vendorStatus: string | null | undefined,
  run: ShipRun | undefined,
  now = Date.now()
): ShipPhase {
  if (run?.status === 'running') return 'running';
  if (run?.status === 'succeeded') return 'shipped';
  if (vendorStatus === 'shipped' || vendorStatus === 'customer_pickup' || vendorStatus === 'completed') {
    return 'shipped';
  }
  if (
    run?.status === 'failed' &&
    !run.outcomeUnknown &&
    now - (run.finishedAt ?? now) < FAILED_RUN_TTL_MS
  ) {
    return 'failed';
  }
  // No run, a stale failure, or a request whose answer never arrived: the order says `shipping`,
  // so keep checking it.
  return 'pending';
}

/** A failed run whose draft the Shipping page for `orderId` should put back, if any. */
export const restorableShipDraft = (run: ShipRun | undefined, orderId?: string | null) =>
  run?.status === 'failed' &&
  run.draft &&
  // A bundle's form is the same whichever member's page it is opened from.
  (run.kind === 'bundle' || run.orderId === String(orderId)) &&
  Date.now() - (run.finishedAt ?? 0) < FAILED_RUN_TTL_MS
    ? run.draft
    : undefined;

/** A carrier taking this long is unusual enough to say the wait is not stuck on our side. */
export const SLOW_SHIP_SECONDS = 20;

/** Seconds since `since`, ticking once a second while `active`. */
export const useElapsedSeconds = (since: number | undefined, active: boolean) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return undefined;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [active, since]);
  return since ? Math.max(0, Math.floor((now - since) / 1000)) : 0;
};
